import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  deleteIssueRelated,
  getIssueRelated,
  getQueryIssues,
  getQueryIssuesParams,
  saveIssueRelated,
} from '../../../../../packages/common/api';
import ChevronIcon from '../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import DeleteIconBig from '../../../../../packages/common/shared-ui/src/icons/DeleteIconBig';
import PlusIcon from '../../../../../packages/common/shared-ui/src/icons/PlusIcon';
import SubmitIcon from '../../../../../packages/common/shared-ui/src/icons/SubmitIcon';
import { useTranslation } from '../../../../../packages/common/utils/getLocalString';

import { relationsSelectValues } from './relationsSelectValues';

// Original class: relatedRow
const StyledRelatedRow = styled(Grid)(theme => ({
  '& .MuiGrid-item': {
    display: 'flex',
    alignItems: 'center',
    '& h5': {
      flexShrink: 0,
      display: 'inline-flex',
    },
    '& .MuiFormControl-root.MuiTextField-root.formTextField': {
      width: '156px',
      '& .MuiInputBase-root.MuiOutlinedInput-root .MuiInputBase-input.MuiOutlinedInput-input':
        {
          padding: '11px 16px',
        },
    },
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '12px',
    lineHeight: '20px',
    marginTop: '-5px',
    transform: 'none',
    left: '10px',
    top: '15px',
    '&.Mui-focused': {
      transform: 'none',
      left: '10px',
      top: '15px',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select)(theme => ({
  '&.MuiInputBase-root.MuiOutlinedInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '10px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    border: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: filterSelect
const StyledFilterSelect = styled(Autocomplete)(theme => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '10px 65px 10px 10px',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '12px',
      lineHeight: '20px',
      color: '#212346',
      padding: '0',
      height: 'auto',
    },
    '&.Mui-disabled': {
      '& .MuiAutocomplete-input': {
        color: '#E4E4EF',
        '&::placeholder': {
          color: '#E4E4EF',
        },
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#E4E4EF',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root path': {
          fill: '#E4E4EF',
        },
      },
    },
  },

  '&.MuiAutocomplete-hasClearIcon': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderColor: '#3448FF',
      '&:hover': {
        borderColor: '#3448FF',
      },
    },
  },

  '& .MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    top: '-5px',
    zIndex: 99,
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },

  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },

  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    //width: '100%!important',
    maxWidth: '300px!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      border: '1px solid #E4E4EF',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-loading': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: hoveredIcon
const StyledHoveredIcon = styled(IconButton)(theme => ({
  '& .MuiSvgIcon-root': {
    '& path': {
      transition: '.2s ease',
    },
  },

  '&:hover': {
    '& .MuiSvgIcon-root': {
      '& path': {
        fill: '#7174AC',
      },
    },
  },
}));
// Original class: rowLink
const StyledRowLink = styled(Link)(theme => ({
  transition: '.4s ease',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

const IssueRelated = ({ permissions }) => {
  const getLocalString = useTranslation();
  const params = useParams();
  const [related, setRelated] = useState([]);
  const [showAddRelated, setShowAddRelated] = useState(false);
  const [relatedIssueOptions, setRelatedIssueOptions] = useState([]);
  const [relatedIssueLoading, setRelatedIssueLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function getRelated() {
      const { data: related } = await getIssueRelated(params.id);
      if (!ignore) {
        return related;
      }
    }
    getRelated()
      .then(res => {
        setRelated(res);
      })
      .catch(error => console.error(error, 'ERROR IN GET ISSUE'));
    return () => {
      ignore = true;
    };
  }, [params.id]);

  const handleRelatedIssueChange = useCallback(
    async value => {
      setRelatedIssueLoading(true);
      let query = {
        search: value,
        related: params.id,
      };
      try {
        const response = await getQueryIssuesParams(query);
        if (response) {
          setRelatedIssueLoading(false);
          setRelatedIssueOptions(response.data);
        }
      } catch (error) {
        console.error('ERROR WITH GET', error);
      }
    },
    [params.id],
  );

  const handleRelationSelect = useCallback(
    (event, index) => {
      const delay = ['precedes', 'follows'];
      let updatedObject = {
        ...related[index],
        relationType: event.target.value,
        relatedIssueId: '',
      };
      !updatedObject.canDelay &&
        delay.includes(event.target.value) &&
        (updatedObject.canDelay = true);
      if (updatedObject.canDelay && !delay.includes(event.target.value)) {
        delete updatedObject.canDelay;
        delete updatedObject.delay;
      }
      setRelated([
        ...related.slice(0, index),
        updatedObject,
        ...related.slice(index + 1),
      ]);
    },
    [related],
  );
  const handleRelatedIssueSelect = useCallback(
    (value, index) => {
      const updatedObject = { ...related[index], relatedIssueId: value };
      setRelated([
        ...related.slice(0, index),
        updatedObject,
        ...related.slice(index + 1),
      ]);
    },
    [related],
  );
  const handleTextField = useCallback(
    (event, type, index) => {
      const updatedObject = { ...related[index], [type]: event.target.value };
      setRelated([
        ...related.slice(0, index),
        updatedObject,
        ...related.slice(index + 1),
      ]);
    },
    [related],
  );
  const handleNumberField = useCallback(
    (event, type, field) => {
      const reg = new RegExp(/^[0-9-]*$/);
      if (reg.test(event.target.value)) {
        handleTextField(event, type, field);
      }
    },
    [handleTextField],
  );

  const handleSaveIssueRelated = useCallback(
    async index => {
      if (!related[index].relatedIssueId || !related[index].relationType) {
        return;
      }
      let data = {
        relatedIssueId: related[index].relatedIssueId.valueId,
        relationType: related[index].relationType,
      };
      related[index].delay && (data.delay = related[index].delay);
      try {
        const response = await saveIssueRelated(params.id, data);
        let updatedObject = { ...related[index] };
        if (response) {
          delete updatedObject.new;
          const { data: related } = response;
          setRelated(related);
          setShowAddRelated(false);
          setError(false);
        }
      } catch (error) {
        error.response.status === 500 && setError(true);
        console.error('ERROR WITH SAVE', error);
      }
    },
    [params.id, related],
  );

  const handleDeleteIssueRelated = useCallback(
    async index => {
      if (related[index].new) {
        const newArray = related.filter((value, i) => i !== index);
        setRelated(newArray);
        setShowAddRelated(false);
        return;
      }
      try {
        const response = await deleteIssueRelated(
          params.id,
          related[index].relationId,
        );
        if (response) {
          const newArray = related.filter((value, i) => i !== index);
          setRelated(newArray);
        }
      } catch (error) {
        console.error('ERROR WITH DELETE', error);
      }
    },
    [params.id, related],
  );
  const handleShowAddRelated = useCallback(() => {
    setRelated([
      { new: true, relatedIssueId: '', relationType: '' },
      ...related,
    ]);
    setShowAddRelated(true);
  }, [related]);

  return (
    <>
      <Grid
        container
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid>
          <Typography
            variant='h3'
            sx={{
              color: '#082253',
              fontWeight: 600,
            }}
          >
            {getLocalString('IssuePage', 'sections', 'related', 'title')}
          </Typography>
        </Grid>
        <Grid>
          {permissions.canManageRelations && (
            <IconButton
              onClick={handleShowAddRelated}
              sx={{ padding: '6px 4px' }}
              disabled={showAddRelated}
            >
              <PlusIcon width='24' height='24' viewBox='0 0 24 24' />
            </IconButton>
          )}
        </Grid>
      </Grid>
      <Grid container direction={'column'} sx={{ position: 'relative' }}>
        {error && (
          <Box sx={{ position: 'absolute', top: '-10px', left: 0 }}>
            <Typography
              variant='h3'
              sx={{
                color: '#E03737',
              }}
            >
              {getLocalString('errors', 'relatedError')}
            </Typography>
          </Box>
        )}

        {related.length > 0 &&
          related.map((item, index) => {
            if (item.new || item.edit) {
              return (
                <StyledRelatedRow
                  key={index}
                  container
                  columnSpacing={4}
                  sx={{
                    marginTop: '10px',
                  }}
                >
                  <Grid>
                    <FormControl sx={{ width: '100%' }}>
                      <StyledSelectLabel id='select-label'>
                        {getLocalString('labels', 'select')}
                      </StyledSelectLabel>
                      <StyledRowSelect
                        labelId='select-label'
                        onChange={event => handleRelationSelect(event, index)}
                        variant='outlined'
                        sx={{ width: '180px' }}
                        value={related[index].relationType}
                        MenuProps={{
                          slots: { paper: StyledPaperDropdown },
                          anchorOrigin: {
                            vertical: 'center',
                            horizontal: 'center',
                          },
                        }}
                        IconComponent={props => (
                          <ChevronIcon direction={'down'} {...props} />
                        )}
                      >
                        {relationsSelectValues.map((item, index) => (
                          <MenuItem key={index} value={item.valueId}>
                            {getLocalString(
                              'IssuePage',
                              'sections',
                              'related',
                              'labels',
                              item.valueId,
                            )}
                          </MenuItem>
                        ))}
                      </StyledRowSelect>
                    </FormControl>
                  </Grid>
                  <Grid
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant='h5'
                      sx={{
                        color: '#41424E',
                        marginRight: '5px',
                      }}
                    >
                      {`${getLocalString('labels', 'issue')} #`}
                    </Typography>
                    <StyledFilterSelect
                      noOptionsText={getLocalString('labels', 'notFound')}
                      loadingText={getLocalString('labels', 'loading')}
                      filterOptions={x => x}
                      onChange={(event, newValue) =>
                        handleRelatedIssueSelect(newValue, index)
                      }
                      disablePortal
                      options={relatedIssueOptions}
                      disableClearable
                      sx={{ width: '156px' }}
                      loading={relatedIssueLoading}
                      onBlur={() => setRelatedIssueOptions([])}
                      renderOption={(props, option) => {
                        return (
                          <MenuItem
                            {...props}
                            key={option.valueId}
                            value={option.valueId}
                          >
                            {option.valueName}
                          </MenuItem>
                        );
                      }}
                      getOptionLabel={option => option.valueName || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.value === value.value
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          onChange={ev =>
                            handleRelatedIssueChange(ev.target.value)
                          }
                          placeholder={getLocalString('labels', 'select')}
                        />
                      )}
                      popupIcon={<ChevronIcon direction='down'></ChevronIcon>}
                      value={item.relatedIssueId}
                      slots={{
                        popper: props => (
                          <Popper {...props} placement='bottom-start'></Popper>
                        ),
                      }}
                    />
                  </Grid>
                  {item.canDelay && (
                    <Grid
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        variant='h5'
                        sx={{
                          color: '#41424E',
                          marginRight: '5px',
                        }}
                      >
                        {`${getLocalString('labels', 'delay')}:`}
                      </Typography>
                      <TextField
                        onChange={event =>
                          handleNumberField(event, 'delay', index)
                        }
                        placeholder={getLocalString('labels', 'input')}
                        fullWidth
                        variant='outlined'
                        required
                        value={related[index].delay ? related[index].delay : ''}
                        className={'formTextField labelLeft'}
                        slotProps={{
                          inputLabel: { shrink: false },
                        }}
                      />
                      <Typography
                        variant='h5'
                        sx={{
                          color: '#41424E',
                          marginLeft: '5px',
                        }}
                      >
                        {getLocalString('labels', 'days')}
                      </Typography>
                    </Grid>
                  )}
                  <Grid>
                    <Grid container>
                      <Grid>
                        <IconButton
                          onClick={() => handleSaveIssueRelated(index)}
                          sx={{ padding: '8px', height: '100%' }}
                        >
                          <SubmitIcon />
                        </IconButton>
                      </Grid>
                      <Grid>
                        <StyledHoveredIcon
                          onClick={() => handleDeleteIssueRelated(index)}
                          sx={{ padding: '8px', height: '100%' }}
                        >
                          <DeleteIconBig />
                        </StyledHoveredIcon>
                      </Grid>
                    </Grid>
                  </Grid>
                </StyledRelatedRow>
              );
            } else {
              return (
                <Grid
                  key={index}
                  container
                  sx={{
                    alignItems: 'center',
                    marginTop: '10px',
                    lineHeight: '20px',
                  }}
                >
                  <Grid>
                    <Typography
                      variant='h4'
                      sx={{
                        color: '#212346',
                      }}
                    >
                      {`${item.relationName} `}
                      {item.delay !== undefined &&
                        item.delay !== 0 &&
                        `${item.delay} ${getLocalString('labels', 'days')} `}
                      <StyledRowLink
                        to={`${window.location.origin}/issues/${item.relatedIssueId}`}
                        target={'_blank'}
                      >
                        {item.relatedIssue}
                      </StyledRowLink>
                    </Typography>
                  </Grid>
                  <Grid sx={{ display: 'flex' }}>
                    <StyledHoveredIcon
                      sx={{ padding: '0 8px' }}
                      onClick={() => handleDeleteIssueRelated(index)}
                    >
                      <DeleteIconBig />
                    </StyledHoveredIcon>
                  </Grid>
                </Grid>
              );
            }
          })}
      </Grid>
    </>
  );
};

export default IssueRelated;
